import { useState, useEffect, useMemo, useRef } from "react";
import { supabase } from "./supabase";

// ★ SMART HOURS (owner request 2026-10-05): how many hours did a staff member
//   actually work each day, measured from what they did in Elyon — not from
//   what they report. Activity = a click/keystroke in the CRM (one ping per
//   active minute), a call logged in the call queue, a booking, a cancellation,
//   a payment, or an SMS they sent. Activity closer together than the idle
//   limit is one working stretch; a longer silence is a break and isn't paid.

const C = {
  bg:"#f8f5f0", surface:"#ffffff", border:"#e8e4df",
  borderGold:"rgba(180,140,60,0.3)", goldBg:"rgba(180,140,60,0.08)",
  gold:"#b48c3c", goldD:"#8a6f2e",
  green:"#2e8b57", greenL:"rgba(46,139,87,0.12)",
  red:"#c0392b", redL:"rgba(192,57,43,0.10)",
  orange:"#d97706", orangeL:"rgba(217,119,6,0.10)",
  text:"#1a1a2e", muted:"#6b7280", dim:"#9ca3af", light:"#f3f4f6",
};
const F = "'Montserrat',sans-serif";
const S = "'Playfair Display','Cormorant Garamond',Georgia,serif";
const TZ = "America/New_York"; // the clinic's clock, whoever is viewing
const DOW = ["Mon","Tue","Wed","Thu","Fri","Sat","Sun"];
// A phone conversation can run 10+ minutes with no click, so anything under
// 15 minutes of silence still counts as working. Adjustable on screen.
const DEFAULT_IDLE_MIN = 15;
const CALL_TYPES = ["call_started", "no_answer", "callback_scheduled"];

// ── Heartbeat ────────────────────────────────────────────────────────────
// Writes one row per minute in which the user actually touched the CRM.
// An open-but-untouched tab writes nothing.
export function useActivityHeartbeat(profile, tab) {
  const lastInteraction = useRef(0);
  const tabRef = useRef(tab);
  tabRef.current = tab;
  useEffect(() => {
    if (!profile?.id) return;
    const mark = () => { lastInteraction.current = Date.now(); };
    const events = ["mousedown", "keydown", "wheel", "touchstart", "mousemove"];
    events.forEach(e => window.addEventListener(e, mark, { passive: true }));
    const timer = setInterval(() => {
      if (Date.now() - lastInteraction.current > 60000) return;
      supabase.from("staff_activity_pings")
        .insert({ user_id: profile.id, tab: tabRef.current || null })
        .then(({ error }) => { if (error) console.warn("[heartbeat]", error.message); });
    }, 60000);
    return () => { clearInterval(timer); events.forEach(e => window.removeEventListener(e, mark)); };
  }, [profile?.id]);
}

// ── Time helpers (clinic time zone) ──────────────────────────────────────
const tzOffsetMin = (d) =>
  (Date.parse(d.toLocaleString("en-US", { timeZone: "UTC" })) - Date.parse(d.toLocaleString("en-US", { timeZone: TZ }))) / 60000;
// UTC instant of local midnight at the start of `dateStr` (YYYY-MM-DD) in TZ
const tzMidnight = (dateStr) => {
  const [y, m, d] = dateStr.split("-").map(Number);
  const guess = new Date(Date.UTC(y, m - 1, d, 12));
  return new Date(Date.UTC(y, m - 1, d) + tzOffsetMin(guess) * 60000);
};
const localDate = (d) => d.toLocaleDateString("en-CA", { timeZone: TZ });
const addDays = (dateStr, n) => {
  const [y, m, d] = dateStr.split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, d + n)).toISOString().slice(0, 10);
};
const mondayOf = (dateStr) => {
  const [y, m, d] = dateStr.split("-").map(Number);
  const dow = new Date(Date.UTC(y, m - 1, d)).getUTCDay(); // 0 = Sun
  return addDays(dateStr, -((dow + 6) % 7));
};
const fmtTime = (ms) => new Date(ms).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", timeZone: TZ });
const fmtHours = (min) => `${Math.floor(min / 60)}h ${String(Math.round(min % 60)).padStart(2, "0")}m`;
const fmtDay = (dateStr) => new Date(dateStr + "T12:00:00Z").toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric", timeZone: "UTC" });

async function fetchAll(build) {
  const out = [];
  for (let from = 0; ; from += 1000) {
    const { data, error } = await build().range(from, from + 999);
    if (error) throw error;
    out.push(...(data || []));
    if (!data || data.length < 1000) return out;
  }
}

// Turn timestamps into working stretches: activity within `idleMin` of the
// previous activity continues the stretch; each stretch gets 1 minute of
// credit for its final action.
function buildSessions(times, idleMin) {
  const ts = [...times].sort((a, b) => a - b);
  const sessions = [];
  for (const t of ts) {
    const cur = sessions[sessions.length - 1];
    if (cur && t - cur.end <= idleMin * 60000) { cur.end = t; cur.count++; }
    else sessions.push({ start: t, end: t, count: 1 });
  }
  const worked = sessions.reduce((s, x) => s + (x.end - x.start) / 60000 + 1, 0);
  const breaks = sessions.slice(1).map((s, i) => ({ from: sessions[i].end, to: s.start }));
  return { sessions, breaks, worked };
}

export default function StaffHours() {
  const [staff, setStaff] = useState([]);
  const [userId, setUserId] = useState(null);
  const [weekStart, setWeekStart] = useState(mondayOf(localDate(new Date())));
  const [days, setDays] = useState([true, true, true, true, true, true, true]);
  const [idleMin, setIdleMin] = useState(DEFAULT_IDLE_MIN);
  const [useHistory, setUseHistory] = useState(true);
  const [reported, setReported] = useState({});
  const [events, setEvents] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    supabase.from("profiles").select("id,full_name,role,status").eq("status", "active").order("full_name")
      .then(({ data }) => {
        const list = (data || []).filter(p => !["admin", "owner"].includes((p.role || "").toLowerCase()));
        setStaff(list);
        const ruby = list.find(p => /ruby/i.test(p.full_name || ""));
        setUserId((ruby || list.find(p => p.role === "frontdesk") || list[0])?.id || null);
      });
  }, []);

  const person = staff.find(p => p.id === userId);
  const dates = useMemo(() => DOW.map((_, i) => addDays(weekStart, i)), [weekStart]);

  useEffect(() => {
    if (!person) return;
    let cancelled = false;
    (async () => {
      setLoading(true); setError("");
      try {
        const from = tzMidnight(weekStart).toISOString();
        const to = tzMidnight(addDays(weekStart, 7)).toISOString();
        const name = person.full_name;
        const [pings, acts, booked, cancels, pays, sms, anonCalls] = await Promise.all([
          fetchAll(() => supabase.from("staff_activity_pings").select("at").eq("user_id", person.id).gte("at", from).lt("at", to).order("at")),
          fetchAll(() => supabase.from("lead_activities").select("created_at,activity_type").eq("user_id", person.id).gte("created_at", from).lt("created_at", to).order("created_at")),
          fetchAll(() => supabase.from("appointments").select("booked_at").eq("booked_by_user_name", name).gte("booked_at", from).lt("booked_at", to).order("booked_at")),
          fetchAll(() => supabase.from("appointments").select("cancelled_at").eq("cancelled_by_name", name).gte("cancelled_at", from).lt("cancelled_at", to).order("cancelled_at")),
          fetchAll(() => supabase.from("payments").select("created_at").eq("created_by_name", name).gte("created_at", from).lt("created_at", to).order("created_at")),
          fetchAll(() => supabase.from("messages").select("created_at").eq("sent_by_name", name).gte("created_at", from).lt("created_at", to).order("created_at")),
          fetchAll(() => supabase.from("lead_activities").select("created_at,activity_type").is("user_id", null).in("activity_type", CALL_TYPES).gte("created_at", from).lt("created_at", to).order("created_at")),
        ]);
        if (cancelled) return;
        const ev = [];
        pings.forEach(r => ev.push({ t: Date.parse(r.at), kind: "crm" }));
        acts.forEach(r => ev.push({ t: Date.parse(r.created_at), kind: CALL_TYPES.includes(r.activity_type) ? "call" : "lead" }));
        booked.forEach(r => ev.push({ t: Date.parse(r.booked_at), kind: "booking" }));
        cancels.forEach(r => ev.push({ t: Date.parse(r.cancelled_at), kind: "cancel" }));
        pays.forEach(r => ev.push({ t: Date.parse(r.created_at), kind: "payment" }));
        sms.forEach(r => ev.push({ t: Date.parse(r.created_at), kind: "sms" }));
        anonCalls.forEach(r => ev.push({ t: Date.parse(r.created_at), kind: "anon_call" }));
        setEvents(ev);
      } catch (e) {
        if (!cancelled) setError(e.message || String(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [person, weekStart]);

  const rows = useMemo(() => {
    if (!events) return [];
    return dates.map((date, i) => {
      const dayEv = events.filter(e => localDate(new Date(e.t)) === date);
      const own = dayEv.filter(e => e.kind !== "anon_call");
      // Before tracking existed, calls carried no name. A day with no tracked
      // activity at all can fall back to the anonymous call log — flagged as
      // an estimate, because another staff member's calls may be mixed in.
      const tracked = own.some(e => e.kind === "crm" || e.kind === "call" || e.kind === "lead");
      const estimate = !tracked && useHistory && dayEv.some(e => e.kind === "anon_call");
      const used = estimate ? dayEv : own;
      const { sessions, breaks, worked } = buildSessions(used.map(e => e.t), idleMin);
      const count = (k) => used.filter(e => e.kind === k).length;
      return {
        date, dow: DOW[i], on: days[i], sessions, worked, estimate,
        breaks: breaks.filter(b => b.to - b.from > idleMin * 60000),
        calls: count("call") + count("anon_call"), bookings: count("booking"),
        crmMin: count("crm"), other: count("cancel") + count("payment") + count("sms") + count("lead"),
      };
    });
  }, [events, dates, days, idleMin, useHistory]);

  const selected = rows.filter(r => r.on);
  const totalWorked = selected.reduce((s, r) => s + r.worked, 0);
  const totalReported = selected.reduce((s, r) => s + (Number(reported[r.date]) || 0) * 60, 0);
  const anyReported = selected.some(r => reported[r.date] !== undefined && reported[r.date] !== "");

  const card = { background: C.surface, border: "1px solid " + C.border, borderRadius: 14, padding: 18 };
  const btn = { padding: "8px 14px", borderRadius: 8, border: "1px solid " + C.border, background: C.surface, cursor: "pointer", fontFamily: F, fontSize: 12, fontWeight: 600, color: C.text };
  const label = { fontSize: 10, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 6 };
  const TL_START = 8 * 60, TL_END = 22 * 60; // timeline 8am–10pm

  return (
    <div style={{ fontFamily: F, color: C.text, padding: "4px 0" }}>
      <div style={{ fontSize: 26, fontWeight: 700, fontFamily: S, marginBottom: 4 }}>Smart Hours</div>
      <div style={{ fontSize: 13, color: C.muted, marginBottom: 18, maxWidth: 760, lineHeight: 1.5 }}>
        Hours worked, measured from real activity in Elyon: clicks and typing, calls, bookings, cancellations, payments and texts.
        Any silence longer than the idle limit counts as a break and isn't counted. Times are clinic time (New York).
      </div>

      <div style={{ ...card, display: "flex", flexWrap: "wrap", gap: 20, alignItems: "flex-end", marginBottom: 16 }}>
        <div>
          <div style={label}>Staff member</div>
          <select value={userId || ""} onChange={e => setUserId(e.target.value)} style={{ ...btn, minWidth: 180 }}>
            {staff.map(p => <option key={p.id} value={p.id}>{p.full_name} ({p.role})</option>)}
          </select>
        </div>
        <div>
          <div style={label}>Week</div>
          <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
            <button style={btn} onClick={() => setWeekStart(addDays(weekStart, -7))}>←</button>
            <input type="date" value={weekStart} onChange={e => e.target.value && setWeekStart(mondayOf(e.target.value))} style={btn} />
            <button style={btn} onClick={() => setWeekStart(addDays(weekStart, 7))}>→</button>
          </div>
        </div>
        <div>
          <div style={label}>Days</div>
          <div style={{ display: "flex", gap: 4 }}>
            {DOW.map((d, i) => (
              <button key={d} onClick={() => setDays(days.map((v, j) => j === i ? !v : v))}
                style={{ ...btn, padding: "8px 10px", background: days[i] ? C.gold : C.surface, color: days[i] ? "#fff" : C.muted, borderColor: days[i] ? C.gold : C.border }}>{d}</button>
            ))}
          </div>
        </div>
        <div>
          <div style={label}>Idle limit</div>
          <select value={idleMin} onChange={e => setIdleMin(Number(e.target.value))} style={btn}>
            {[5, 10, 15, 20, 30].map(m => <option key={m} value={m}>{m} min{m === DEFAULT_IDLE_MIN ? " (recommended)" : ""}</option>)}
          </select>
        </div>
        <label style={{ display: "flex", gap: 6, alignItems: "center", fontSize: 12, color: C.muted, cursor: "pointer", maxWidth: 260 }}>
          <input type="checkbox" checked={useHistory} onChange={e => setUseHistory(e.target.checked)} />
          On days before tracking began, estimate from the unnamed call log
        </label>
      </div>

      <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 16 }}>
        <div style={{ ...card, flex: "1 1 180px" }}>
          <div style={label}>Worked (selected days)</div>
          <div style={{ fontSize: 28, fontWeight: 700, fontFamily: S, color: C.goldD }}>{fmtHours(totalWorked)}</div>
        </div>
        {anyReported && (
          <>
            <div style={{ ...card, flex: "1 1 180px" }}>
              <div style={label}>Reported</div>
              <div style={{ fontSize: 28, fontWeight: 700, fontFamily: S }}>{fmtHours(totalReported)}</div>
            </div>
            <div style={{ ...card, flex: "1 1 180px", background: totalReported - totalWorked > 30 ? C.redL : C.greenL }}>
              <div style={label}>Reported − worked</div>
              <div style={{ fontSize: 28, fontWeight: 700, fontFamily: S, color: totalReported - totalWorked > 30 ? C.red : C.green }}>
                {totalReported - totalWorked >= 0 ? "+" : "−"}{fmtHours(Math.abs(totalReported - totalWorked))}
              </div>
            </div>
          </>
        )}
      </div>

      {error && <div style={{ ...card, background: C.redL, color: C.red, marginBottom: 16 }}>Couldn't load activity: {error}</div>}
      {loading && <div style={{ color: C.muted, fontSize: 13, marginBottom: 12 }}>Loading activity…</div>}

      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {rows.map(r => (
          <div key={r.date} style={{ ...card, opacity: r.on ? 1 : 0.4, padding: 14 }}>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 16, alignItems: "center" }}>
              <div style={{ width: 120 }}>
                <div style={{ fontWeight: 700, fontSize: 14 }}>{fmtDay(r.date)}</div>
                {r.estimate && <div style={{ fontSize: 10, fontWeight: 700, color: C.orange, background: C.orangeL, borderRadius: 4, padding: "2px 6px", display: "inline-block", marginTop: 4 }}>ESTIMATE</div>}
              </div>
              <div style={{ width: 90 }}>
                <div style={{ fontSize: 20, fontWeight: 700, fontFamily: S, color: r.worked ? C.goldD : C.dim }}>{r.worked ? fmtHours(r.worked) : "—"}</div>
              </div>
              <div style={{ width: 150, fontSize: 12, color: C.muted }}>
                {r.sessions.length ? `${fmtTime(r.sessions[0].start)} – ${fmtTime(r.sessions[r.sessions.length - 1].end)}` : "No activity"}
              </div>
              <div style={{ fontSize: 11, color: C.muted, flex: "1 1 200px" }}>
                {r.calls} calls · {r.bookings} bookings · {r.crmMin} active min in CRM{r.other ? ` · ${r.other} other` : ""}
              </div>
              <label style={{ fontSize: 11, color: C.muted, display: "flex", alignItems: "center", gap: 6 }}>
                Reported
                <input type="number" min="0" step="0.25" placeholder="hrs" value={reported[r.date] ?? ""}
                  onChange={e => setReported({ ...reported, [r.date]: e.target.value })}
                  style={{ ...btn, width: 64, padding: "6px 8px" }} />
              </label>
            </div>
            {r.sessions.length > 0 && (
              <div style={{ marginTop: 10 }}>
                <div style={{ position: "relative", height: 14, background: C.light, borderRadius: 7, overflow: "hidden" }}>
                  {r.sessions.map((s, i) => {
                    const toMin = (t) => { const d = new Date(t); const [h, m] = d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", timeZone: TZ }).split(":").map(Number); return h * 60 + m; };
                    const a = Math.max(TL_START, toMin(s.start)), b = Math.min(TL_END, toMin(s.end) + 1);
                    if (b <= a) return null;
                    return <div key={i} title={`${fmtTime(s.start)} – ${fmtTime(s.end)}`} style={{ position: "absolute", top: 0, bottom: 0, left: ((a - TL_START) / (TL_END - TL_START) * 100) + "%", width: Math.max(0.4, (b - a) / (TL_END - TL_START) * 100) + "%", background: r.estimate ? C.orange : C.gold }} />;
                  })}
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: 9, color: C.dim, marginTop: 2 }}>
                  {["8a", "10a", "12p", "2p", "4p", "6p", "8p", "10p"].map(t => <span key={t}>{t}</span>)}
                </div>
                {r.breaks.length > 0 && (
                  <div style={{ fontSize: 11, color: C.red, marginTop: 6 }}>
                    Breaks: {r.breaks.map(b => `${fmtTime(b.from)}–${fmtTime(b.to)} (${Math.round((b.to - b.from) / 60000)}m)`).join(" · ")}
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
